import type { XmaxOpenClient } from '../api/client'
import type { FileClient } from '../files/client'
import { XMAX_SDK_BUILD_REGION, type XmaxSdkRegion } from '../build-config'
import type { SDKI18n } from '../i18n'
import { throwIfAborted } from '../shared/abort'
import { notifyError, type XmaxErrorNotifier } from '../shared/error-utils'
import { XmaxSdkError } from '../shared/sdk-error'
import {
  linkAbortSignal,
  normalizeUid,
  resolvePositiveNumber,
  resolveReferenceImageUrl,
  resolveSourceUrl,
  validateSubmitOptions,
  waitForPollDelay,
  type ThrowOfflineError,
} from './client-helpers'
import type {
  OfflineBatchQueryResult,
  OfflineClient,
  OfflineGenerateOptions,
  OfflineListOptions,
  OfflineRequestOptions,
  OfflineSubmitOptions,
  OfflineTask,
  OfflineTaskPage,
} from './types'

export const XMAX_OFFLINE_API_BASE_URLS: Record<XmaxSdkRegion, string> = {
  cn: 'https://api.xmaxai.com/open/api/v1',
  global: 'https://api.xmax.ai/open/api/v1',
}

/** Official Offline Task API URL for the region this package was built for. */
export const XMAX_OFFLINE_API_PRODUCTION_BASE_URL =
  XMAX_OFFLINE_API_BASE_URLS[XMAX_SDK_BUILD_REGION]

export const DEFAULT_OFFLINE_POLL_INTERVAL_MS = 2_000
export const DEFAULT_OFFLINE_POLL_TIMEOUT_MS = 30 * 60 * 1_000
const OFFLINE_TASK_STATUSES = new Set(['submitted', 'processing', 'completed', 'error'])

type OfflineClientContext = {
  i18n: SDKI18n
  onError?: XmaxErrorNotifier
}

type OfflineSubmitRequest = {
  prompt: string
  refVideoPath: string
  refImagePath?: string
  quality: NonNullable<OfflineSubmitOptions['quality']>
  fps?: NonNullable<OfflineSubmitOptions['fps']>
}

/** @internal Create the offline capability exposed by {@link XmaxClient.offline}. */
export function createOfflineClient(
  apiClient: XmaxOpenClient,
  files: FileClient,
  context: OfflineClientContext,
): OfflineClient {
  const throwOfflineError: ThrowOfflineError = (code, messageKey, params) => {
    const error = new XmaxSdkError(code, context.i18n.t(messageKey, params))
    throw notifyError(error, context.onError, { code, message: error.message })
  }

  const submit = async (options: OfflineSubmitOptions): Promise<OfflineTask> => {
    throwIfAborted(options.signal)
    if (!options.model?.name.trim()) {
      throwOfflineError('INVALID_MODEL', 'errors.modelEmpty')
    }
    validateSubmitOptions(options, throwOfflineError)
    const prompt = options.context.prompt.trim()
    if (!prompt || prompt.length > 1_024) {
      throwOfflineError('INVALID_INPUT', 'errors.offlinePromptInvalid')
    }

    const uploadOptions = options.signal ? { signal: options.signal } : undefined
    const [refVideoPath, refImagePath] = await Promise.all([
      resolveSourceUrl(files, options.source, uploadOptions, throwOfflineError),
      resolveReferenceImageUrl(
        files,
        options.context.refImageUrl,
        uploadOptions,
        throwOfflineError,
      ),
    ])
    throwIfAborted(options.signal)

    const body: OfflineSubmitRequest = {
      prompt,
      refVideoPath,
      ...(refImagePath ? { refImagePath } : {}),
      quality: options.quality ?? 'hd',
      ...(options.fps === undefined ? {} : { fps: options.fps }),
    }
    return apiClient.request<OfflineTask>('POST', '/offline-task', body, {
      signal: options.signal,
    })
  }

  const status = async (
    taskUid: string,
    options: OfflineRequestOptions = {},
  ): Promise<OfflineTask> => {
    throwIfAborted(options.signal)
    return apiClient.request<OfflineTask>(
      'GET',
      `/offline-task/${encodeURIComponent(normalizeUid(taskUid, throwOfflineError))}`,
      undefined,
      options,
    )
  }

  const batchQuery = async (
    taskUids: readonly string[],
    options: OfflineRequestOptions = {},
  ): Promise<OfflineBatchQueryResult> => {
    throwIfAborted(options.signal)
    const normalized = taskUids.map((taskUid) => normalizeUid(taskUid, throwOfflineError))
    if (normalized.length === 0 || normalized.length > 100) {
      throwOfflineError('INVALID_INPUT', 'errors.offlineTaskUidsInvalid')
    }
    return apiClient.request<OfflineBatchQueryResult>(
      'POST',
      '/offline-task/batch-query',
      { taskUids: normalized },
      options,
    )
  }

  const list = async (options: OfflineListOptions = {}): Promise<OfflineTaskPage> => {
    const { pageNumber = 1, pageSize = 10, status: taskStatus, signal } = options
    throwIfAborted(signal)
    if (!Number.isInteger(pageNumber) || pageNumber < 1) {
      throwOfflineError('INVALID_INPUT', 'errors.offlinePageNumberInvalid')
    }
    if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 100) {
      throwOfflineError('INVALID_INPUT', 'errors.offlinePageSizeInvalid')
    }
    if (taskStatus && !OFFLINE_TASK_STATUSES.has(taskStatus)) {
      throwOfflineError('INVALID_INPUT', 'errors.offlineStatusInvalid')
    }
    const query = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    })
    if (taskStatus) query.set('status', taskStatus)
    return apiClient.request<OfflineTaskPage>(
      'GET',
      `/offline-task/page?${query.toString()}`,
      undefined,
      { signal },
    )
  }

  const generate = async (options: OfflineGenerateOptions): Promise<OfflineTask> => {
    const pollIntervalMs = resolvePositiveNumber(
      options.pollIntervalMs,
      DEFAULT_OFFLINE_POLL_INTERVAL_MS,
      'pollIntervalMs',
      throwOfflineError,
    )
    const timeoutMs = resolvePositiveNumber(
      options.timeoutMs,
      DEFAULT_OFFLINE_POLL_TIMEOUT_MS,
      'timeoutMs',
      throwOfflineError,
    )
    throwIfAborted(options.signal)

    const operationController = new AbortController()
    let timedOut = false
    const unlinkSignal = linkAbortSignal(options.signal, operationController)
    const timeoutId = globalThis.setTimeout(() => {
      timedOut = true
      operationController.abort()
    }, timeoutMs)

    try {
      let task = await submit({
        ...options,
        signal: operationController.signal,
      })
      let reportedStatus: OfflineTask['status'] | undefined
      const reportStatus = () => {
        if (task.status === reportedStatus) return
        reportedStatus = task.status
        options.onStatusChange?.(task)
      }
      reportStatus()

      while (task.status !== 'completed' && task.status !== 'error') {
        await waitForPollDelay(pollIntervalMs, operationController.signal)
        task = await status(task.uid, { signal: operationController.signal })
        reportStatus()
      }
      return task
    } catch (error) {
      if (timedOut && !options.signal?.aborted) {
        throwOfflineError(
          'OFFLINE_TASK_TIMEOUT',
          'errors.offlineTaskTimeout',
          { timeoutMs },
        )
      }
      throw error
    } finally {
      globalThis.clearTimeout(timeoutId)
      unlinkSignal()
    }
  }

  return { generate, submit, status, batchQuery, list }
}
